import { Link } from 'react-router';
import { useCurrentUser } from '@/auth/AuthContext';
import { Alert } from '@/components/ui';
import { SaleStatusCell } from '@/components/SaleStatusCell';
import { GoalBar, Kpi, MonthPicker, RefreshButton } from '@/components/widgets';
import { formatBRL, formatCompetencia, formatDate, formatDayMonth, formatPct, nomeMes, tempoDesde } from '@/lib/format';
import type { MeDashboard } from '@/lib/types';
import { useApi } from '@/lib/useApi';
import { useMonth } from '@/lib/useMonth';

/** Painel do vendedor (também usado pelo gestor/admin que vende: "Meu desempenho"). */
export function VendorDashboard() {
  const user = useCurrentUser();
  const [mes, setMes] = useMonth();
  const { data: d, error, reload } = useApi<MeDashboard>(`/dashboard/me?competencia=${mes}`, 60_000);
  const primeiroNome = user.nome.split(' ')[0];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Olá, {primeiroNome}</h1>
          <p className="muted">
            Seu resultado em {formatCompetencia(mes)}
            {d && !d.fechado && <>, atualizado {tempoDesde(d.ultimaSync)} com o Conta Azul</>}.
          </p>
        </div>
        <div className="row">
          <MonthPicker value={mes} onChange={setMes} />
          {!d?.fechado && <RefreshButton onDone={() => void reload()} />}
        </div>
      </header>

      {error && <Alert>{error}</Alert>}
      {!d ? (
        !error && <p className="muted">Carregando…</p>
      ) : (
        <>
          {d.fechado && <Alert kind="info">Este mês está fechado. Os valores abaixo são os definitivos.</Alert>}

          {!d.fechado && d.resultado.pendentes > 0 && (
            <div className="callout">
              <div>
                <strong>
                  {d.resultado.pendentes} venda{d.resultado.pendentes > 1 ? 's' : ''} aguardando o custo
                </strong>
                <p className="muted small">
                  Sem o custo, a comissão dessas vendas não é calculada.{' '}
                  {d.prazoEncerrado ? (
                    <>O prazo de {nomeMes(mes)} terminou em {formatDate(d.prazoCusto)}. Fale com o gestor.</>
                  ) : (
                    <>
                      Prazo para {nomeMes(mes)}: <strong>{formatDate(d.prazoCusto)}</strong>.
                    </>
                  )}
                </p>
              </div>
              {!d.prazoEncerrado && (
                <Link className="btn btn--primary" to={`/vendas?mes=${mes}&filtro=pendentes`}>
                  Informar custos
                </Link>
              )}
            </div>
          )}

          <dl className="kpis">
            <Kpi label="Vendido no mês" value={formatBRL(d.resultado.vendido)} hint={`${d.resultado.qtdVendas} venda${d.resultado.qtdVendas === 1 ? '' : 's'}`} />
            <Kpi label="Lucro das vendas" value={formatBRL(d.resultado.lucro)} hint={`${d.resultado.qtdComCusto} com custo informado`} />
            <Kpi
              label="Comissão"
              value={formatBRL(d.resultado.comissao)}
              hint={d.resultado.pendentes > 0 && !d.fechado ? `Pode subir com ${d.resultado.pendentes} custo(s) pendente(s)` : undefined}
            />
            <Kpi label={d.fechado ? 'Bônus' : 'Bônus previsto'} value={formatBRL(d.resultado.bonusTotal)} hint={`${formatPct(d.resultado.atingimento)} da meta`} tone="ok" />
          </dl>

          <div className="grid-2">
            <section className="card">
              <div className="card__head">
                <h2>Minha meta: {formatBRL(d.meta.metaIndividual)}</h2>
                <strong className="big-pct">{formatPct(d.resultado.atingimento)}</strong>
              </div>
              <GoalBar atingimento={d.resultado.atingimento} piso={Number(d.politica.pisoPct)} label="Atingimento da minha meta" />
              <p className="muted small">A partir de {formatPct(d.politica.pisoPct)} da meta, você já recebe bônus.</p>
              <p>
                {d.resultado.faixa === 'ABAIXO_DO_PISO' && (
                  <>
                    Faltam <strong>{formatBRL(d.resultado.faltaParaPiso)}</strong> para começar a ganhar bônus.
                  </>
                )}
                {d.resultado.faixa === 'PROPORCIONAL' && (
                  <>
                    Faltam <strong>{formatBRL(d.resultado.faltaParaMeta)}</strong> para bater a meta e garantir o bônus completo. Depois disso, cada nova
                    venda aumenta o seu bônus.
                  </>
                )}
                {d.resultado.faixa === 'ACELERADOR' && <>Meta batida! Cada nova venda continua aumentando o seu bônus.</>}
              </p>
            </section>

            <section className="card">
              <h2>{d.fechado ? 'Resultado do mês' : 'Se o mês fechasse hoje'}</h2>
              {!d.fechado && <p className="muted small">Previsão. O valor final sai no fechamento do gestor.</p>}
              <dl className="breakdown">
                <div>
                  <dt>Comissão</dt>
                  <dd>{formatBRL(d.resultado.comissao)}</dd>
                </div>
                <div>
                  <dt>Bônus da meta individual</dt>
                  <dd>{formatBRL(d.resultado.bonusIndividual)}</dd>
                </div>
                <div>
                  <dt>Bônus por superar a meta</dt>
                  <dd>{formatBRL(d.resultado.bonusAcelerador)}</dd>
                </div>
                <div>
                  <dt>Bônus da meta da equipe</dt>
                  <dd>{formatBRL(d.resultado.bonusColetivo)}</dd>
                </div>
                <div className="breakdown__total">
                  <dt>{d.fechado ? 'Total' : 'Total previsto'}</dt>
                  <dd>{formatBRL(d.resultado.total)}</dd>
                </div>
              </dl>
            </section>
          </div>

          <section className="card">
            <div className="card__head">
              <h2>Meta da equipe: {formatBRL(d.equipe.metaGlobal)}</h2>
              <strong className="big-pct">{formatPct(d.equipe.atingimento)}</strong>
            </div>
            <GoalBar atingimento={d.equipe.atingimento} piso={1} label="Atingimento da meta da equipe" />
            <p>
              {d.equipe.atingida ? (
                <>
                  A equipe vendeu <strong>{formatBRL(d.equipe.vendido)}</strong> e bateu a meta: todos os vendedores ganham{' '}
                  <strong>+{formatBRL(d.politica.valorColetivo)}</strong>.
                </>
              ) : (
                <>
                  A equipe vendeu <strong>{formatBRL(d.equipe.vendido)}</strong>. Se chegar a {formatBRL(d.equipe.metaGlobal)} até o fim do mês, todos os
                  vendedores ganham <strong>+{formatBRL(d.politica.valorColetivo)}</strong>.
                </>
              )}
            </p>
          </section>

          <section className="card flush">
            <div className="card__head pad-h">
              <h2>Últimas vendas</h2>
              <Link to={`/vendas?mes=${mes}`}>Ver todas as vendas</Link>
            </div>
            {d.ultimasVendas.length === 0 ? (
              <p className="muted pad">Nenhuma venda sua neste mês ainda.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Venda</th>
                      <th scope="col">Cliente</th>
                      <th scope="col" className="num">Valor</th>
                      <th scope="col" className="num">Custo</th>
                      <th scope="col" className="num">Lucro</th>
                      <th scope="col" className="num">Comissão</th>
                      <th scope="col">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.ultimasVendas.map((v) => (
                      <tr key={v.id} className={v.status === 'CANCELADA' ? 'row--off row--strike' : undefined}>
                        <td>
                          <Link to={`/vendas/${v.id}`}>#{v.numero ?? '—'}</Link> <span className="muted small">{formatDayMonth(v.dataVenda)}</span>
                        </td>
                        <td>{v.clienteNome}</td>
                        <td className="num">{formatBRL(v.valorBruto)}</td>
                        <td className="num">{v.custoTotal ? formatBRL(v.custoTotal) : '—'}</td>
                        <td className="num">{v.lucro ? formatBRL(v.lucro) : '—'}</td>
                        <td className="num">{v.comissaoValor ? formatBRL(v.comissaoValor) : '—'}</td>
                        <td>
                          <SaleStatusCell sale={{ ...v, editavel: !d.fechado && !d.prazoEncerrado }} mine />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
